import { useMemo } from 'react';
import { z } from 'zod';
import { Card } from '@/components/ui/card';
import { getNotificationRenderer } from '@/components/notifications/notificationRenderers';
import { extractActions } from '@/components/notifications/notificationActionDispatch';
import { NotificationActionButtons } from '@/components/notifications/NotificationActions';
import { NotificationRecordOpenButton } from '@/components/notifications/NotificationRecordOpenButton';

// Mirrors the backend list_my_notifications result. Local (single consumer).
const resultSchema = z.object({
  notifications: z.array(
    z.object({
      id: z.string(),
      eventType: z.string(),
      notifiableType: z.string().nullable(),
      notifiableId: z.string().nullable(),
      payload: z.record(z.string(), z.unknown()),
    }),
  ),
});

interface NotificationsToolResultProps {
  /** The list_my_notifications tool result (data lives in the result, not args). */
  result: unknown;
}

/**
 * Renders the AI's `list_my_notifications` result as a compact list, reusing
 * the app's notification renderers for titles/icons and deep-links. The model
 * summarizes in prose; these cards handle navigation.
 */
export function NotificationsToolResult({
  result,
}: NotificationsToolResultProps) {
  const parsed = useMemo(() => resultSchema.safeParse(result), [result]);

  // Nothing to render (tool errored, or no notifications) — the model's prose
  // reply already covers "you're all caught up".
  if (!parsed.success || parsed.data.notifications.length === 0) return null;

  return (
    <Card>
      <ul className="flex flex-col gap-2.5">
        {parsed.data.notifications.map((n) => {
          const renderer = getNotificationRenderer(n.eventType);
          const Icon = renderer.getIcon?.(n.payload) ?? renderer.Icon;
          const link = renderer.getLink(
            n.notifiableType,
            n.notifiableId,
            n.payload,
          );
          const actions = extractActions(n.payload);
          return (
            <li key={n.id} className="flex items-start gap-2">
              <Icon className="text-muted-foreground mt-0.5 h-4 w-4 shrink-0" />
              <div className="flex min-w-0 flex-1 flex-col gap-1">
                <span className="text-card-foreground text-sm">
                  {renderer.getTitle(n.payload)}
                </span>
                <span className="text-muted-foreground text-xs">
                  {renderer.getBody(n.payload)}
                </span>
                {(link || actions.length > 0) && (
                  <div className="flex flex-wrap gap-1.5">
                    {link && <NotificationRecordOpenButton href={link} />}
                    {actions.length > 0 && (
                      <NotificationActionButtons
                        actions={actions}
                        className="flex flex-wrap gap-1.5"
                      />
                    )}
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}
